/**
 * The psalter page model — shared by the site that renders the pages, the
 * script that prerenders them, and the script that builds the per-psalm PDFs.
 *
 * Same split as `hymnal-pages.ts` and `propers-pages.ts`: route enumeration is a
 * property of the DATA, display is a property of the site. `generate-seo-routes.mjs`
 * writes the routes the prerenderer builds and the app is what serves them, so if
 * the two computed slugs independently the build would emit pages the router cannot
 * match, or the router would offer pages that were never prerendered.
 *
 * Pure and data-injected: nothing here imports a dataset.
 *
 * ## The two halves of a verse are the asterisk, and that is ALL they are
 *
 * `bcp_psalter.json` gives every verse exactly two strings, and it is worth being
 * precise about what that division is, because it is easy to over-read:
 *
 *   "1": [ "Happy are they who have not walked in the counsel of the wicked,",
 *          "nor lingered in the way of sinners, nor sat in the seats of the scornful!" ]
 *
 * That break is the BCP's **asterisk** — the mid-verse division for antiphonal
 * recitation, marking where one side stops and the other answers. It is NOT
 * Anglican chant pointing. Pointing says which syllables fall on the reciting note
 * and which on the cadence; the asterisk says nothing about syllables at all. The
 * BCP carries the asterisk and no pointing, and every published pointing of the
 * 1979 text is under copyright (see `docs/pm/active/canticle-liturgical-resource-registry.md`).
 * A per-org pointing overlay is the planned answer; this model deliberately holds
 * only what the book prints.
 *
 * ## Rights
 *
 * The 1979 BCP text, the Psalter included, is public domain in the United States —
 * unlike the RCL citation table that says which psalm is appointed when, which is
 * © 1992 Consultation on Common Texts. See `data/lectionary/PROVENANCE.md`. This
 * file models only the psalter, which is why it needs no permission; the join to
 * liturgical days lives in `psalm-appointments.ts` and is gated there, not here.
 */
/**
 * `LORD` and `GOD` are printed in small capitals in the BCP, standing for the
 * Tetragrammaton. The scrape lost the small-caps span but left its footprint: a
 * stray space where the span closed, which is why 366 verses read `the LORD ,`.
 * Collapsing space before punctuation is therefore not cosmetic tidying — it is
 * undoing a known, mechanical artifact of the extraction.
 *
 * The small caps themselves are a DISPLAY decision and stay in the site's CSS;
 * the model keeps the words as the book has them, in capitals, so a consumer that
 * cannot render small caps still reads correct text.
 */
export function normalizePsalmText(s) {
    return s
        .replace(/\s+([,;:.!?])/g, '$1')
        .replace(/\s{2,}/g, ' ')
        .trim();
}
/**
 * Verse keys arrive as strings from JSON, and `Object.keys` on a numeric-keyed
 * object is insertion-ordered rather than sorted — Psalm 119's 176 verses would
 * come back in whatever order the scrape wrote them. Sorting numerically is what
 * makes the output deterministic, which is what keeps SSR and browser renders
 * byte-identical and hydration quiet.
 */
function orderedVerses(raw) {
    return Object.entries(raw.verses)
        .map(([n, halves]) => ({ n: Number(n), halves }))
        .filter((v) => Number.isFinite(v.n))
        .sort((a, b) => a.n - b.n)
        .map(({ n, halves }) => ({
        n,
        // The dataset is uniformly two halves (verified across all 2,500 verses by
        // psalter-pages.test.ts). Anything else is corrupt input, so pad rather than
        // throw: one malformed verse should not blank a whole psalm at build time.
        halves: [normalizePsalmText(halves[0] ?? ''), normalizePsalmText(halves[1] ?? '')],
    }));
}
/** Build every psalm page from the raw dataset, in psalm order. */
export function enumeratePsalms(source, raw) {
    const psalms = Object.entries(raw)
        .map(([key, p]) => ({ number: Number(key), p }))
        .filter(({ number }) => Number.isInteger(number) && number >= 1 && number <= 150)
        .sort((a, b) => a.number - b.number)
        .map(({ number, p }) => {
        const verses = orderedVerses(p);
        return {
            number,
            slug: `psalm-${number}`,
            title: p.heading?.trim() || `Psalm ${number}`,
            latinTitle: normalizePsalmText(p.latin_title ?? ''),
            verses,
            verseCount: verses.length,
        };
    });
    return { source, psalms };
}
/** Every concrete route the psalter produces, index first. */
export function psalterRoutes(pages, base = '/hymnal/psalter') {
    return [base, ...pages.psalms.map((p) => `${base}/${p.slug}`)];
}
/**
 * The psalm NUMBER out of a lectionary citation — the whole of what a link needs.
 *
 * `"Psalm 116:1-2, 12-19"` → `116`. A citation names a portion, but a link points
 * at the psalm, exactly as a printed psalter does: you open to 116 and sing the
 * marked verses. No book has ever contained only the appointed portion, so nothing
 * here parses the ranges. That parser is real work and belongs with bulletin
 * typesetting, where printing all 19 verses of a psalm you sing 9 of is actually
 * wrong; until then it would be a fiddly dependency on the critical path of a
 * feature that does not need it.
 *
 * Returns `null` rather than throwing: lectionary citations are scraped data and a
 * malformed one should drop a link, not fail a build.
 */
export function psalmNumberFromCitation(citation) {
    const m = (citation ?? '').trim().match(/^(?:psalms?\s+)?(\d{1,3})\b/i);
    if (!m)
        return null;
    const n = Number(m[1]);
    return n >= 1 && n <= 150 ? n : null;
}
